import { z } from "zod";

/**
 * Team member tags (plans/team-member-tags-spec.md).
 *
 * A business-defined, customer-facing label on a team member — e.g. a
 * language note — with an optional longer description that booking surfaces
 * show before and after booking. Openings owns the text; each surface decides
 * where to place it. Not `CustomerTag`, which gates customers.
 */
export const MEMBER_TAG_LABEL_MAX = 24;
export const MEMBER_TAG_DESCRIPTION_MAX = 280;
export const MEMBER_TAGS_PER_MEMBER_MAX = 3;

/**
 * The public shape on every member payload that carries tags. Ordered by tag
 * creation. Servers always send the array (empty when none); it is optional
 * so older fixtures and consumers stay valid.
 */
export const publicMemberTagSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().nullable(),
});
export type PublicMemberTag = z.infer<typeof publicMemberTagSchema>;

export const publicMemberTagsSchema = z.array(publicMemberTagSchema);

/**
 * The key two labels share when a customer would read them as the same tag:
 * trimmed, inner whitespace collapsed, lower-cased. `MemberTag.labelKey` is
 * unique per business, so the database refuses a duplicate even when two
 * saves race.
 */
export function memberTagLabelKey(label: string): string {
  return label.trim().replace(/\s+/g, " ").toLowerCase();
}

/** Owner input when creating or editing a tag definition. */
export const memberTagInputSchema = z
  .object({
    label: z
      .string()
      .trim()
      .min(1, "Enter a label")
      .max(
        MEMBER_TAG_LABEL_MAX,
        `Keep the label to ${MEMBER_TAG_LABEL_MAX} characters`,
      ),
    description: z
      .string()
      .trim()
      .max(
        MEMBER_TAG_DESCRIPTION_MAX,
        `Keep the description to ${MEMBER_TAG_DESCRIPTION_MAX} characters`,
      )
      .nullish()
      .transform((value) => (value ? value : null)),
  })
  .strict();
export type MemberTagInput = z.input<typeof memberTagInputSchema>;
export type MemberTagValues = z.output<typeof memberTagInputSchema>;

/** The full set of tags on one member; replaces what is there. */
export const memberTagIdsSchema = z
  .array(z.string().min(1))
  .max(
    MEMBER_TAGS_PER_MEMBER_MAX,
    `A team member can have up to ${MEMBER_TAGS_PER_MEMBER_MAX} tags`,
  )
  .refine((ids) => new Set(ids).size === ids.length, "Each tag once");
